import {action, computed, makeObservable, observable, ObservableMap, runInAction} from 'mobx';
import {QuickBetLineModel, QuickBetLinePayload, QuickBetLinePatch} from 'app/store/QuickBetLineModel';
import {FilterStore} from 'app/store/filters/FilterStore';
import EventInterface from 'app/interfaces/EventInterface';
import {STATUS_COMPLETED, STATUS_CANCEL_COMPLETED} from 'admin/components/bet_form/utils/LineConsts';

export interface RealtimeBatch {
    upsert?: QuickBetLinePayload[];
    patch?: Array<{id: number} & QuickBetLinePatch>;
    remove?: number[];
}

export class QuickBetStore {
    @observable lines: ObservableMap<number, QuickBetLineModel> = new ObservableMap();

    @observable lineIds: number[] = [];

    filterStore: FilterStore | null = null;

    @observable isLoading: boolean = false;

    @observable isInitialized: boolean = false;

    @observable error: string | null = null;

    constructor(filterStore: FilterStore) {
        this.filterStore = filterStore;
        makeObservable(this);
    }

    @computed
    get linesList(): QuickBetLineModel[] {
        return this.lineIds
            .map(id => this.lines.get(id))
            .filter((m): m is QuickBetLineModel => typeof m !== 'undefined');
    }

    @computed
    get totalCount(): number {
        return this.lineIds.length;
    }

    getLine(id: number): QuickBetLineModel | undefined {
        return this.lines.get(id);
    }

    @action
    setPlacedGamerForLine(lineId: number, gamer: number): void {
        this.lines.get(lineId)?.setPlacedGamer(gamer);
    }

    hasLine(id: number): boolean {
        return this.lines.has(id);
    }

    @action
    setInitialData(payload: QuickBetLinePayload[]): void {
        this.lines.clear();
        this.lineIds = payload.map(p => p.id);
        payload.forEach(p => this.lines.set(p.id, new QuickBetLineModel(p)));
        this.isInitialized = true;
        this.updateFilterEvents(payload);
    }

    private updateFilterEvents(payload: QuickBetLinePayload[]): void {
        if (!this.filterStore) {
            return;
        }

        const events: EventInterface[] = [];

        payload.forEach(line => {
            if (!events.find(event => event.tourn === line.tourn && event.filter === line.filter)) {
                events.push({
                    filter: line.filter,
                    game: line.game,
                    tourn: line.tourn
                });
            }
        });
        this.filterStore.update({events});
    }

    @action
    replaceAll(payload: QuickBetLinePayload[]): void {
        this.setInitialData(payload);
    }

    @action
    upsertLine(payload: QuickBetLinePayload): void {
        const existing = this.lines.get(payload.id);

        if (existing) {
            existing.updateFromPatch(payload);
        } else {
            this.lines.set(payload.id, new QuickBetLineModel(payload));
            this.lineIds.push(payload.id);
        }
    }

    @action
    upsertLines(payload: QuickBetLinePayload[]): void {
        payload.forEach(p => this.upsertLine(p));
    }

    @action
    removeLine(id: number): void {
        this.lines.delete(id);
        this.lineIds = this.lineIds.filter(x => x !== id);
    }

    @action
    handleWsLineUpdate(payload: QuickBetLinePayload): void {
        const {id, quick, deleted, hidden, coef_1, coef_2, status, color, date, quick_timer_end_at} = payload;
        const lineFinished = status === STATUS_COMPLETED || status === STATUS_CANCEL_COMPLETED;

        if (quick && !lineFinished) {
            if (deleted || hidden) {
                this.removeLine(id);
            } else if (this.hasLine(id)) {
                this.upsertLine({coef_1, coef_2, color, date, id, quick_timer_end_at, status} as QuickBetLinePayload);
            } else {
                this.upsertLine(payload);
            }
        } else if (this.hasLine(id)) {
            this.removeLine(id);
        }
    }

    @action
    applyRealtimeUpdates(batch: RealtimeBatch): void {
        runInAction(() => {
            batch.upsert?.forEach(p => this.upsertLine(p));
            batch.patch?.forEach(({id, ...patch}) => {
                this.lines.get(id)?.updateFromPatch(patch);
            });
            batch.remove?.forEach(id => this.removeLine(id));
        });
    }

    @action
    clear(): void {
        this.lines.clear();
        this.lineIds = [];
        this.isInitialized = false;
        this.error = null;
    }

    @action
    setLoading(value: boolean): void {
        this.isLoading = value;
    }

    @action
    setError(error: string | null): void {
        this.error = error;
    }
}
